import * as React from "react";
import { useMappedState } from "redux-react-hook";

import type { IStore } from "src/core/shared/store";
import { IProductMaybeWithSubstitution } from "src/core/products/utils/filter-product-substitutions";
import { Text } from "src/library/components/Text";
import { ProductCardLayoutDetail } from "src/core/shared/store/global/global.reducer";
import OrderVariantLabel from "src/core/orders/components/orderVariantLabel";

import { IQuantityProps } from "./ProductQuantityButton";

export interface IProductComponentLayoutDetailsProps extends Partial<IQuantityProps> {
  product: IProductMaybeWithSubstitution;
  customOnly?: boolean;
}

export const ProductComponentLayoutDetails: React.ReactFCWithChildren<IProductComponentLayoutDetailsProps> = ({ product, customOnly = false }) => {
  const { componentLayout } = useMappedState(({ global }: IStore) => ({ componentLayout: global.settings?.componentLayout }));
  const productCardLayout = componentLayout?.productCard;

  const getDetail = React.useCallback((detail: ProductCardLayoutDetail) => {
    if (customOnly && !detail.includes("custom:")) {
      return null;
    }

    switch (detail) {
      case "shelfLife": {
        if (product.shelfLife) {
          return (
            <Text
              color="secondary"
              className="shelf-life"
            >
              {`Shelf life: ${product.shelfLife} ${product.shelfLifePeriod || "days"}`}
            </Text>
          );
        }

        return null;
      }
      case "variant": {
        return product.orderVariant && (
          <div className="order-variant-label-wrapper">
            <OrderVariantLabel orderVariant={product.orderVariant} />
          </div>
        );
      }

      default: {
        if (detail.includes("custom:")) {
          const customDataNameKey = detail.replace("custom:", "");
          const customDataItem = product.customData?.find(item => item.name === customDataNameKey);

          if (customDataItem && customDataItem.value) {
            return (
              <Text>
                {`${customDataItem.description || customDataItem.name}: ${customDataItem.value}`}
              </Text>
            );
          }

          return null;
        }

        return null;
      }
    }
  }, [
    customOnly,
    product.customData,
    product.orderVariant,
    product.shelfLife,
    product.shelfLifePeriod
  ]);

  if (!productCardLayout?.layout.details) {
    return null;
  }

  return (
    <>
      {productCardLayout?.layout.details.map((detail, index) => (
        <div
          key={`${detail.replace(/\s/g, "")}-${index}`}
          className={`product-detail-configurable-item ${detail}`}
        >
          {getDetail(detail)}
        </div>
      ))}
    </>
  );
};
